import { useEffect, useRef } from 'react';

/** Keys that scroll. Space is listed by its key value, which is " ". */
const SCROLL_KEYS = new Set([
  ' ', 'PageUp', 'PageDown', 'End', 'Home',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
]);

const FAILSAFE_MS = 6000;

/**
 * Holds the page at the top while the hero choreographs itself on load.
 * Scrolling through the sequence mid-flight lands the visitor in a
 * half-drawn page.
 *
 * Every exit path routes through the same release, and a hard timer
 * guarantees it runs even if the caller never reports the sequence
 * finishing: an unscrollable page is a far worse failure than a skipped
 * animation.
 *
 * @param {boolean} active whether the lock should currently be held
 * @param {() => void} onRelease called if the visitor escapes out of it
 */
export function useIntroLock(active, onRelease) {
  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;

  useEffect(() => {
    if (!active) return undefined;

    const root = document.documentElement;

    const escape = () => onReleaseRef.current?.();

    const onKeydown = (e) => {
      // An escape hatch that costs nothing and prevents a felt trap.
      if (e.key === 'Escape') {
        escape();
        return;
      }
      if (SCROLL_KEYS.has(e.key)) e.preventDefault();
    };
    const onWheel = (e) => e.preventDefault();
    const onTouchMove = (e) => {
      // Single-finger drags are pans and get blocked; anything with more
      // fingers is a pinch, and taking zoom away is not on the table.
      if (e.touches.length === 1) e.preventDefault();
    };
    // overflow:hidden stops user scrolling but not programmatic scrolls, and
    // it leaks on iOS Safari. Pinning the offset is the one guarantee that
    // holds regardless of how the scroll was initiated.
    const onScroll = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    root.setAttribute('data-intro-locked', '');
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scroll', onScroll, true);

    const failsafe = setTimeout(escape, FAILSAFE_MS);

    return () => {
      clearTimeout(failsafe);
      root.removeAttribute('data-intro-locked');
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('wheel', onWheel, { passive: false });
      document.removeEventListener('touchmove', onTouchMove, { passive: false });
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [active]);
}

/**
 * Whether the intro is worth locking for at all. Read once on mount, not
 * reactively — this is a question about how the page was arrived at.
 */
export function shouldRunIntro(reducedMotion) {
  // The sequence resolves instantly under reduced motion, so there is
  // nothing to wait for.
  if (reducedMotion) return false;
  if (typeof window === 'undefined') return false;
  // Restored mid-page or deep-linked: yanking the visitor back to the top
  // to watch an intro would be worse than skipping it.
  if (window.scrollY > 0) return false;
  if (window.location.hash) return false;
  return true;
}
